import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { useConfirmation } from '../contexts/ConfirmationContext';
import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Textarea';

const NEWSLETTER_WEBHOOK_URL = 'https://n8n.koll.cc/webhook/82a41a90-8706-4407-af0e-2dc012998f36';

interface NewsletterResponse {
  html?: string;
  text?: string;
}

const NewsletterPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');
  const confirm = useConfirmation();
  const [copied, setCopied] = useState({ html: '', text: '' });
  const dirty = htmlContent !== copied.html || textContent !== copied.text;

  const handleCreateNewsletter = async () => {
    if (isLoading) return;
    if (dirty && !(await confirm({ title: 'Newsletter neu erstellen?', description: 'Die bisherigen Inhalte werden ersetzt. Kopiere sie bei Bedarf zuerst.', confirmLabel: 'Neu erstellen' }))) return;
    setIsLoading(true);
    try {
      const response = await fetch(NEWSLETTER_WEBHOOK_URL, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Webhook-Fehler (${response.status})`);
      }

      const data = (await response.json()) as NewsletterResponse;
      setHtmlContent(data.html ?? '');
      setTextContent(data.text ?? '');
      toast.success('Newsletter-Inhalt erfolgreich geladen');
    } catch (error) {
      const message = userFacingError(error, 'Unbekannter Fehler');
      toast.error(`Newsletter konnte nicht geladen werden: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async (content: string, label: string) => {
    if (!content) {
      toast.error(`${label} ist leer und konnte nicht kopiert werden`);
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      setCopied(previous => label === 'HTML' ? { ...previous, html: content } : { ...previous, text: content });
      toast.success(`${label} in Zwischenablage kopiert`);
    } catch (error) {
      toast.error(`Kopieren fehlgeschlagen: ${userFacingError(error, 'Unbekannter Fehler')}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <PageHeader title={<>Newsletter</>} description={<>Erstelle den Newsletter für freiheitsfunken.info, prüfe die Inhalte und kopiere sie anschließend.</>} />
          </div>

          <UnsavedChangesNotice dirty={dirty} busy={isLoading} localOnly />
          <Button onClick={handleCreateNewsletter} loading={isLoading} variant="primary">
            Newsletter generieren
          </Button>

          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">HTML</h2>
                <Button
                  onClick={() => handleCopyToClipboard(htmlContent, 'HTML')}
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                >
                  In Zwischenablage kopieren
                </Button>
              </div>
              <Textarea
                aria-label="Newsletter als HTML"
                disabled={isLoading}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                rows={14}
                placeholder="Hier erscheint der HTML-Inhalt..."
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Einleitung</h2>
                <Button
                  onClick={() => handleCopyToClipboard(textContent, 'Text')}
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                >
                  In Zwischenablage kopieren
                </Button>
              </div>
              <Textarea
                aria-label="Newsletter-Einleitung"
                disabled={isLoading}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                rows={14}
                placeholder="Hier erscheint der Text-Inhalt..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPage;
